import { useMemo } from "react";

// Third parties
import { useSelector } from "react-redux";
import identity from "lodash/identity";

// Evergiving
import {
  ContactOptionAction,
  FormConfig,
  FreeFormAddressElementType,
} from "@evergiving/common/dist/es2015/form";

import {
  NameKind,
  Donor,
  PostalAddressKind,
  ContactConsentKind,
  PostalAddress,
} from "@evergiving/common/dist/es2015/pledge";

// Hooks
import { useAppSelector } from "@/lib/hooks/redux";

// Redux
import { RootState, VersionedStringFormState } from "@/lib/redux/types";

// Misc
import { createDetailsPageErrorsSelector } from "@/lib/misc/validations";

export interface PartialDonor extends Omit<Donor, "name"> {
  name: {
    kind: NameKind.FirstLast;
  };
}

interface EssentialDonorFields {
  firstName: string;
  lastName: string;
}

interface PartialDonorFields {
  firstName: string | undefined;
  lastName: string | undefined;
}

type CanSubmitDonorTypeGuard = (
  input: PartialDonorFields
) => input is EssentialDonorFields;

interface UseAfterAddressFieldCommonProps {
  formConfig: FormConfig;
}

interface UseAfterAddressFieldCommonReturn {
  canSubmitDonor: CanSubmitDonorTypeGuard;
  partialDonor: PartialDonor;
  donorFields: PartialDonorFields;
  errors: { [key: string]: boolean };
  freeFormAddressNowComplete: boolean | "always";
  anyFormErrors: boolean;
}

function useFreeFormAddressComplete(
  formConfig: FormConfig
): boolean | "always" {
  const currentCountryCode = useSelector(
    (state: RootState) => state.countryCode.value
  );

  const { address } = formConfig;
  let requiredFields: FreeFormAddressElementType[] = [];
  if (address) {
    const config =
      (currentCountryCode
        ? address.freeForm.byCountry[currentCountryCode]
        : undefined) ?? address.freeForm.defaultConfig; // FIXME
    requiredFields = Object.values(FreeFormAddressElementType).filter(
      (key) => config[key]?.requiredFieldErrorText
    );
  }

  const postalAddressPresent = useSelector((state: RootState): boolean =>
    requiredFields.every(
      (field: keyof VersionedStringFormState) => !!state[field]?.value
    )
  );

  if (requiredFields.length === 0) {
    return "always";
  }

  return postalAddressPresent;
}

export const useAfterAddressFieldCommon = ({
  formConfig,
}: UseAfterAddressFieldCommonProps): UseAfterAddressFieldCommonReturn => {
  const firstName = useSelector((state: RootState) => state.firstName.value);
  const lastName = useSelector((state: RootState) => state.lastName.value);

  const mobilePhoneNumber = useSelector(
    (state: RootState) => state.phoneNumber.value
  );
  const emailAddress = useSelector(
    (state: RootState) => state.emailAddress.value
  );
  const emailAppraisal = useAppSelector(
    (state: RootState) => state.emailAppraisal
  );
  const postalAddress = useSelector((state: RootState) => state.postalAddress);

  const element3 = useSelector((state: RootState) => state.element3.value);
  const element4 = useSelector((state: RootState) => state.element4.value);
  const element5 = useSelector((state: RootState) => state.element5.value);
  const element6 = useSelector((state: RootState) => state.element6.value);
  const element7 = useSelector((state: RootState) => state.element7.value);
  const element8 = useSelector((state: RootState) => state.element8.value);
  const element9 = useSelector((state: RootState) => state.element9.value);
  const element10 = useSelector((state: RootState) => state.element10.value);
  const element11 = useSelector((state: RootState) => state.element11.value);
  const element12 = useSelector((state: RootState) => state.element12.value);
  const element13 = useSelector((state: RootState) => state.element13.value);
  const element14 = useSelector((state: RootState) => state.element14.value);
  const element15 = useSelector((state: RootState) => state.element15.value);

  const phoneContactChecked = useAppSelector(
    (state: RootState) => state.phoneContactChecked
  );
  const smsContactChecked = useAppSelector(
    (state: RootState) => state.smsContactChecked
  );
  const emailContactChecked = useAppSelector(
    (state: RootState) => state.emailContactChecked
  );
  const postContactChecked = useAppSelector(
    (state: RootState) => state.postContactChecked
  );
  const whatsappContactChecked = useAppSelector(
    (state: RootState) => state.whatsappContactChecked
  );

  const dateOfBirth = useAppSelector(
    (state: RootState) => state.dateOfBirth.value
  );
  const gender = useAppSelector((state: RootState) => state.gender.value);
  const profession = useAppSelector(
    (state: RootState) => state.profession.value
  );
  const title = useAppSelector((state: RootState) => state.title.value);
  const belfioreCode = useAppSelector(
    (state: RootState) => state.belfioreCode.value
  );
  const personalIdentificationNumber = useAppSelector(
    (state: RootState) => state.personalIdentificationNumber.value
  );
  const idTypeSighted = useAppSelector(
    (state: RootState) => state.idTypeSighted.value
  );

  const donorFields = useMemo(
    () => ({
      firstName,
      lastName,
    }),
    [firstName, lastName]
  );

  const currentCountryCode = useSelector(
    (state: RootState) => state.countryCode.value
  );

  const custom1 = useSelector((state: RootState) => state.custom1.value);

  const customInputs = useSelector((state: RootState) => state.customInputs);

  const contactOptions = formConfig.contactConsent?.contactOptions;

  let contactConsent;

  if (contactOptions) {
    const switchIfOptOut = (
      isChecked: boolean,
      consentKind: ContactConsentKind
    ) =>
      isChecked !==
      (contactOptions[consentKind]?.action === ContactOptionAction.OptOut);

    contactConsent = {
      phone: switchIfOptOut(phoneContactChecked, ContactConsentKind.Phone),
      sms: switchIfOptOut(smsContactChecked, ContactConsentKind.SMS),
      email: switchIfOptOut(emailContactChecked, ContactConsentKind.Email),
      post: switchIfOptOut(postContactChecked, ContactConsentKind.Post),
      whatsapp: switchIfOptOut(whatsappContactChecked, ContactConsentKind.WhatsApp),
    };
  }

  let partialDonorAddress: PostalAddress | undefined;

  if (postalAddress) {
    if (
      postalAddress.kind === PostalAddressKind.Loqate ||
      postalAddress.kind === PostalAddressKind.Melissa
    ) {
      partialDonorAddress = postalAddress;
    } else {
      partialDonorAddress = {
        kind: PostalAddressKind.FreeForm,
        element2: currentCountryCode ?? formConfig.defaultCountryCode,
        element3,
        element4,
        element5,
        element6,
        element7,
        element8,
        element9,
        element10,
        element11,
        element12,
        element13,
        element14,
        element15,
      };
    }
  } else {
    partialDonorAddress = formConfig.country
      ? {
          kind: PostalAddressKind.FreeForm,
          element2: currentCountryCode ?? formConfig.defaultCountryCode,
          element3: undefined,
          element4: undefined,
          element5: undefined,
          element6: undefined,
          element7: undefined,
          element8: undefined,
          element9: undefined,
          element10: undefined,
          element11: undefined,
          element12: undefined,
          element13: undefined,
          element14: undefined,
          element15: undefined,
        }
      : undefined;
  }

  const partialDonor: PartialDonor = {
    name: {
      kind: NameKind.FirstLast,
    },
    country: currentCountryCode,
    mobilePhoneNumber,
    emailAddress,
    emailAppraisal,
    postalAddress: partialDonorAddress,
    custom1,
    customInputs,
    contactConsent,
    dateOfBirth,
    gender,
    profession,
    title,
    belfioreCode,
    personalIdentificationNumber,
    idTypeSighted,
  };

  const canSubmitDonor: CanSubmitDonorTypeGuard = (
    input: PartialDonorFields
  ): input is EssentialDonorFields =>
    input.firstName !== undefined && input.lastName !== undefined;

  const detailsPageErrorsSelector = useMemo(
    () => createDetailsPageErrorsSelector(formConfig),
    [formConfig]
  );

  const errorsFromValidation = useSelector(detailsPageErrorsSelector);

  // FIXME: are there other fields that need their error state to be merged?
  const isPhoneNumberAlreadyInErrorFromParsingAtFieldLevel = useSelector(
    (state: RootState) => state.errors.phoneNumber
  );
  const errors = useMemo(
    () => ({
      ...errorsFromValidation,
      phoneNumber:
        isPhoneNumberAlreadyInErrorFromParsingAtFieldLevel ||
        errorsFromValidation.phoneNumber,
    }),
    [errorsFromValidation, isPhoneNumberAlreadyInErrorFromParsingAtFieldLevel]
  );

  const freeFormAddressNowComplete = useFreeFormAddressComplete(formConfig);

  const anyFormErrors = Object.values(errors).some(identity);

  return {
    canSubmitDonor,
    partialDonor,
    donorFields,
    errors,
    freeFormAddressNowComplete,
    anyFormErrors,
  };
};
